import { resolveIcon } from '../../styles/icons'
import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import Modal from '../modal/Modal'
import useTranslation from '../../hooks/useTranslation'
import useUserPermissions from '../../hooks/useUserPermissions'
import { APP_REGISTRY } from '../../model/appRegistry.js'

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem;
  justify-content: center;
`

const AppItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: 6rem;
  height: 6rem;
  border-radius: 0.5rem;
  border: 2px solid ${({ $focused, theme }) => $focused ? theme.btn.primary : 'var(--bs-border-color-translucent)'};
  background: ${({ $focused, theme }) => $focused ? theme.card.active : theme.card.modalBackground};
  cursor: pointer;
  font-size: var(--font-S);
  text-align: center;
  padding: 0.35rem;
  overflow: hidden;
  outline: none;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.card.active};
  }
`

const AppLabel = styled.span`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
  font-size: var(--font-XS);
`

const FirstLetter = styled.span`
  text-decoration: underline;
`

const Title = styled.h4`
  margin: 0;
`

function AppSwitcherModal({ zIndex, onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isOwner, apps } = useUserPermissions()
  const [focusedIndex, setFocusedIndex] = useState(0)
  const itemRefs = useRef([])
  const letterCycleRef = useRef({})

  const appList = Object.entries(APP_REGISTRY).map(([appId, app]) => ({
    appId,
    title:     app.title,
    url:       app.url,
    icon:      app.icon,
    ownerOnly: app.ownerOnly,
  }))

  const visibleApps = isOwner
    ? appList
    : appList.filter(app => !app.ownerOnly && apps.includes(app.appId))

  const handleSelect = (url) => {
    onClose()
    navigate(url)
  }

  // Scroll focused item into view on mobile fullscreen
  useEffect(() => {
    itemRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault()
        setFocusedIndex(i => (i + 1) % visibleApps.length)
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault()
        setFocusedIndex(i => (i - 1 + visibleApps.length) % visibleApps.length)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleSelect(visibleApps[focusedIndex].url)
      } else if (e.key.length === 1 && /[a-zA-Z]/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        const letter = e.key.toLowerCase()
        const matches = visibleApps
          .map((app, i) => ({ i, first: t(app.title)[0]?.toLowerCase() }))
          .filter(x => x.first === letter)
          .map(x => x.i)
        if (matches.length > 0) {
          const prev = letterCycleRef.current[letter] ?? -1
          const next = (prev + 1) % matches.length
          letterCycleRef.current[letter] = next
          setFocusedIndex(matches[next])
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [focusedIndex, visibleApps])

  return (
    <Modal
      zIndex={zIndex}
      onClose={onClose}
      show={true}
      mode="fullscreen"
      minHeight="auto"
      opacity={0.4}
    >
      <Modal.Header onClose={onClose}>
        <Title>{t('app.switcher.title')}</Title>
      </Modal.Header>

      <Modal.Body>
        <Grid>
          {visibleApps.map((app, i) => (
            <AppItem
              key={app.appId}
              ref={el => itemRefs.current[i] = el}
              $focused={i === focusedIndex}
              onClick={() => handleSelect(app.url)}
              onMouseEnter={() => setFocusedIndex(i)}
              title={t(app.title)}
            >
              {resolveIcon(app.icon)}
              <AppLabel>
                <FirstLetter>{t(app.title)[0]}</FirstLetter>{t(app.title).slice(1)}
              </AppLabel>
            </AppItem>
          ))}
        </Grid>
      </Modal.Body>
    </Modal>
  )
}

export default AppSwitcherModal
